<template>
  <div class="container-fluid py-4 dashboard">

    <!-- =========================================================
         HEADER
    ========================================================== -->
    <div class="dashboard-header mb-4">

      <div class="header-title">
        <div class="header-icon">
          <i class="bi bi-speedometer2"></i>
        </div>

        <div>
          <h2 class="fw-bold mb-1">Tableau de bord</h2>
          <p class="text-muted mb-0">
            Vue globale de l'activité de la clinique
          </p>
        </div>
      </div>

      <!-- FILTRES -->
      <div class="filters">

        <button
          v-for="periode in periodesPrincipales"
          :key="periode.value"
          class="btn filter-btn"
          :class="filtre === periode.value
            ? 'filter-active'
            : 'filter-normal'"
          @click="changerFiltre(periode.value)"
        >
          {{ periode.label }}
        </button>

        <div class="dropdown">

          <button
            class="btn filter-btn dropdown-toggle"
            :class="periodeSecondaireActive ? 'filter-active' : 'filter-normal'"
            type="button"
            data-bs-toggle="dropdown"
            v-dropdown-fixed
          >
            {{ periodeSecondaireActive ? periodeSecondaireActive.label : 'Plus' }}
          </button>

          <ul class="dropdown-menu dropdown-menu-end">

            <li
              v-for="periode in periodesSecondaires"
              :key="periode.value"
            >
              <a
                href="#"
                class="dropdown-item"
                :class="{ active: filtre === periode.value }"
                @click.prevent="changerFiltre(periode.value)"
              >
                {{ periode.label }}
              </a>
            </li>

          </ul>

        </div>

      </div>

    </div>


    <!-- =========================================================
         ERREUR
    ========================================================== -->
    <div v-if="loadError && !isLoading" class="alert alert-danger d-flex justify-content-between align-items-center mb-4">
      <span>Impossible de charger les statistiques.</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>


    <!-- =========================================================
         LOADER
    ========================================================== -->
    <div
      v-if="isLoading"
      class="loading-container"
    >

      <div class="spinner-border text-primary"></div>

      <p class="mt-3 text-muted">
        Chargement des statistiques...
      </p>

    </div>


    <!-- =========================================================
         DASHBOARD
    ========================================================== -->
    <div v-else-if="!loadError">

      <!-- =======================================================
           PATIENTS
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title primary-title">
          <i class="bi bi-people-fill"></i>
          <span>Patients</span>
        </div>

        <div class="row g-3">

          <div
            v-for="box in patientsKpi"
            :key="box.title"
            class="col-12 col-sm-6 col-lg-3"
          >

            <div class="kpi-card">

              <div
                class="kpi-icon"
                :class="box.bg"
              >
                <i :class="box.icon"></i>
              </div>

              <div>
                <div class="kpi-title">
                  {{ box.title }}
                </div>

                <div class="kpi-value">
                  {{ box.value }}
                </div>
              </div>

            </div>

          </div>

        </div>

      </section>


      <!-- =======================================================
           CONSULTATIONS
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title success-title">
          <i class="bi bi-heart-pulse-fill"></i>
          <span>Consultations</span>
        </div>

        <div class="row g-3">

          <div
            v-for="box in consultationsKpi"
            :key="box.title"
            class="col-12 col-sm-6 col-lg-3"
          >

            <div class="kpi-card">

              <div
                class="kpi-icon"
                :class="box.bg"
              >
                <i :class="box.icon"></i>
              </div>

              <div>

                <div class="kpi-title">
                  {{ box.title }}
                </div>

                <div class="kpi-value">
                  {{ box.value }}
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      <!-- =======================================================
           SERVICES / FINANCES
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title danger-title">
          <i class="bi bi-cash-stack"></i>
          <span>Services & finances</span>
        </div>

        <div class="row g-3">

          <div
            v-for="box in financesKpi"
            :key="box.title"
            class="col-12 col-sm-6 col-lg-3"
          >

            <div class="kpi-card">

              <div
                class="kpi-icon"
                :class="box.bg"
              >
                <i :class="box.icon"></i>
              </div>

              <div>

                <div class="kpi-title">
                  {{ box.title }}
                </div>

                <div class="kpi-value">
                  {{ box.value }}
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      <!-- =======================================================
           HOSPITALISATIONS
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title warning-title">
          <i class="bi bi-hospital-fill"></i>
          <span>Hospitalisations</span>
        </div>

        <div class="row g-3">

          <div
            v-for="box in hospitalisationsKpi"
            :key="box.title"
            class="col-12 col-sm-6 col-lg-3"
          >

            <div
              class="kpi-card"
              :class="box.alert ? 'alert-card' : ''"
            >

              <div
                class="kpi-icon"
                :class="box.bg"
              >
                <i :class="box.icon"></i>
              </div>

              <div>

                <div class="kpi-title">
                  {{ box.title }}
                </div>

                <div class="kpi-value">
                  {{ box.value }}
                </div>

                <span
                  v-if="box.alert"
                  class="badge bg-danger-subtle text-danger mt-2"
                >
                  ⚠️ {{ box.message }}
                </span>

              </div>

            </div>

          </div>

        </div>

      </section>


      <!-- =======================================================
           PHARMACIE
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title info-title">
          <i class="bi bi-capsule-pill"></i>
          <span>Pharmacie</span>
        </div>

        <div class="row g-3">

          <div
            v-for="box in produitsKpi"
            :key="box.title"
            class="col-12 col-sm-6 col-lg-3"
          >

            <div class="kpi-card">

              <div
                class="kpi-icon"
                :class="box.bg"
              >
                <i :class="box.icon"></i>
              </div>

              <div>

                <div class="kpi-title">
                  {{ box.title }}
                </div>

                <div class="kpi-value">
                  {{ box.value }}
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      <!-- =======================================================
           GRAPHIQUES
      ======================================================== -->
      <section class="dashboard-section">

        <div class="section-title chart-title">
          <i class="bi bi-bar-chart-fill"></i>
          <span>Analyse graphique</span>
        </div>


        <div class="row g-4">


          <!-- =================================================
               CONSULTATIONS
          ================================================== -->
          <div class="col-12 col-lg-6">

            <div class="chart-card">

              <div class="chart-header">

                <div>
                  <h5>
                    <i class="bi bi-clipboard2-pulse"></i>
                    Consultations
                  </h5>

                  <small>
                    Répartition des consultations
                  </small>
                </div>

              </div>

              <div class="chart-container">

                <Doughnut
                  v-if="consultationsChartData"
                  :data="consultationsChartData"
                  :options="doughnutOptions"
                />

              </div>

            </div>

          </div>


          <!-- =================================================
               FINANCES
          ================================================== -->
          <div class="col-12 col-lg-6">

            <div class="chart-card">

              <div class="chart-header">

                <div>
                  <h5>
                    <i class="bi bi-currency-exchange"></i>
                    Finances
                  </h5>

                  <small>
                    Montants encaissés
                  </small>
                </div>

              </div>

              <div class="chart-container">

                <Doughnut
                  v-if="financesChartData"
                  :data="financesChartData"
                  :options="doughnutOptions"
                />

              </div>

            </div>

          </div>


          <!-- =================================================
               PHARMACIE
          ================================================== -->
          <div class="col-12 col-lg-6">

            <div class="chart-card">

              <div class="chart-header">

                <div>
                  <h5>
                    <i class="bi bi-capsule"></i>
                    Pharmacie
                  </h5>

                  <small>
                    État des stocks
                  </small>
                </div>

              </div>

              <div class="chart-container">

                <Bar
                  v-if="pharmacieChartData"
                  :data="pharmacieChartData"
                  :options="barOptions"
                />

              </div>

            </div>

          </div>


          <!-- =================================================
               HOSPITALISATIONS
          ================================================== -->
          <div class="col-12 col-lg-6">

            <div class="chart-card">

              <div class="chart-header">

                <div>
                  <h5>
                    <i class="bi bi-hospital"></i>
                    Hospitalisations
                  </h5>

                  <small>
                    Occupation des lits
                  </small>
                </div>

              </div>

              <div class="chart-container">

                <Bar
                  v-if="hospitalisationsChartData"
                  :data="hospitalisationsChartData"
                  :options="barOptions"
                />

              </div>

            </div>

          </div>


        </div>

      </section>

    </div>

  </div>
</template>


<script>

import api from '@/api'

/*
|--------------------------------------------------------------------------
| Chart.js
|--------------------------------------------------------------------------
*/

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
} from 'chart.js'

import {
  Doughnut,
  Bar
} from 'vue-chartjs'


/*
|--------------------------------------------------------------------------
| Enregistrement Chart.js
|--------------------------------------------------------------------------
*/

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
)


export default {

  components: {
    Doughnut,
    Bar
  },


  data() {

    return {

      filtre: 'jour',

      // Démarre à true : évite un flash "dashboard vide (0 partout)"
      // avant que mounted() ne bascule isLoading à true.
      isLoading: true,

      loadError: false,

      /*
      |--------------------------------------------------------------------------
      | Toujours initialiser totals
      |--------------------------------------------------------------------------
      */

      totals: {},


      periodesPrincipales: [

        {
          value: 'jour',
          label: "Aujourd'hui"
        },

        {
          value: 'semaine',
          label: 'Cette semaine'
        },

        {
          value: 'mois',
          label: 'Ce mois-ci'
        },

        {
          value: 'annee',
          label: 'Cette année'
        }

      ],


      periodesSecondaires: [

        {
          value: 'hier',
          label: 'Hier'
        },

        {
          value: 'semaine_derniere',
          label: 'Semaine dernière'
        },

        {
          value: 'mois_dernier',
          label: 'Mois dernier'
        },

        {
          value: 'annee_derniere',
          label: 'Année dernière'
        }

      ]

    }

  },


  computed: {

    /* =========================================================
       PERIODE SECONDAIRE ACTIVE
       Permet au bouton "Plus" de refléter la sélection en cours
       quand elle vient du dropdown (sinon aucun bouton ne montre
       la période réellement affichée).
    ========================================================== */

    periodeSecondaireActive() {

      return this.periodesSecondaires.find(
        p => p.value === this.filtre
      ) || null

    },


    /* =========================================================
       PATIENTS
    ========================================================== */

    patientsKpi() {

      return [

        {
          title: 'Total Patients',
          value: this.totals.patients ?? 0,
          icon: 'bi bi-people',
          bg: 'bg-primary'
        }

      ]

    },


    /* =========================================================
       CONSULTATIONS
    ========================================================== */

    consultationsKpi() {

      const patient =
        Number(this.totals.montant_paye ?? 0)

      const assurance =
        Number(this.totals.montant_paye_assurance ?? 0)

      return [

        {
          title: 'En cours',
          value: this.totals.consultation_en_cours ?? 0,
          icon: 'bi bi-hourglass-split',
          bg: 'bg-warning'
        },

        {
          title: 'En attente',
          value: this.totals.consultations_en_attente ?? 0,
          icon: 'bi bi-clock',
          bg: 'bg-warning'
        },

        {
          title: 'Terminées',
          value: this.totals.consultations_terminees ?? 0,
          icon: 'bi bi-check-circle',
          bg: 'bg-success'
        },

        {
          title: 'Total',
          value: this.totals.total_consultations ?? 0,
          icon: 'bi bi-clipboard-data',
          bg: 'bg-info'
        },

        {
          title: 'Montant payé patient',
          value: this.formatMontant(patient),
          icon: 'bi bi-cash-stack',
          bg: 'bg-primary'
        },

        {
          title: 'Montant Assurance',
          value: this.formatMontant(assurance),
          icon: 'bi bi-building',
          bg: 'bg-secondary'
        },

        {
          title: 'Total payé',
          value: this.formatMontant(patient + assurance),
          icon: 'bi bi-wallet2',
          bg: 'bg-dark'
        }

      ]

    },


    /* =========================================================
       FINANCES
    ========================================================== */

    financesKpi() {

      return [

        {
          title: 'Nombre de factures',
          value: this.totals.nombre_factures ?? 0,
          icon: 'bi bi-receipt',
          bg: 'bg-info'
        },

        {
          title: 'Factures brouillons',
          value: this.totals.factures_brouillon ?? 0,
          icon: 'bi bi-file-earmark-text',
          bg: 'bg-secondary'
        },

        {
          title: 'Factures partiellement payées',
          value: this.totals.factures_partiellement_payee ?? 0,
          icon: 'bi bi-hourglass-split',
          bg: 'bg-warning'
        },

        {
          title: 'Factures payées',
          value: this.totals.factures_payees ?? 0,
          icon: 'bi bi-check-circle',
          bg: 'bg-success'
        },

        {
          title: 'Nombre de services',
          value: this.totals.nombre_services ?? 0,
          icon: 'bi bi-list-check',
          bg: 'bg-primary'
        },

        {
          title: 'Services annulés',
          value: this.totals.services_annules ?? 0,
          icon: 'bi bi-x-circle',
          bg: 'bg-danger'
        },

        {
          title: 'Montant encaissé Assurance',
          value: this.formatMontant(
            this.totals.montant_encaisse_assurance ?? 0
          ),
          icon: 'bi bi-building',
          bg: 'bg-primary'
        },

        {
          title: 'Montant encaissé Patient',
          value: this.formatMontant(
            this.totals.montant_encaisse_patient ?? 0
          ),
          icon: 'bi bi-cash-coin',
          bg: 'bg-success'
        },

        {
          title: 'Montant encaissé',
          value: this.formatMontant(
            this.totals.montant_encaisse ?? 0
          ),
          icon: 'bi bi-currency-exchange',
          bg: 'bg-dark'
        },

        {
          title: 'Montant à rembourser',
          value: this.formatMontant(
            this.totals.remboursement ?? 0
          ),
          icon: 'bi bi-arrow-counterclockwise',
          bg: 'bg-danger'
        }

      ]

    },


    /* =========================================================
       HOSPITALISATIONS
    ========================================================== */

    hospitalisationsKpi() {

      const sortieDepassee =
        this.totals.nbre_hospi_sortie_prevue ?? 0

      return [

        {
          title: 'En cours',
          value: this.totals.hospitalisations_en_cours ?? 0,
          icon: 'bi bi-hospital',
          bg: 'bg-warning'
        },

        {
          title: 'Total Chambres',
          value: this.totals.total_chambres ?? 0,
          icon: 'bi bi-building',
          bg: 'bg-primary'
        },

        {
          title: 'Chambres occupées',
          value: this.totals.chambres_occupees ?? 0,
          icon: 'bi bi-door-closed',
          bg: 'bg-danger'
        },

        {
          title: 'Total lits',
          value: this.totals.total_lits ?? 0,
          icon: 'bi bi-bed',
          bg: 'bg-primary'
        },

        {
          title: 'Lits occupés',
          value: this.totals.lits_occupes ?? 0,
          icon: 'bi bi-person-fill',
          bg: 'bg-danger'
        },

        {
          title: 'Lits libres',
          value: this.totals.lits_libres ?? 0,
          icon: 'bi bi-bed',
          bg: 'bg-success'
        },

        {
          title: 'Montant payé',
          value: this.formatMontant(
            this.totals.montant_paye_hospitalisation ?? 0
          ),
          icon: 'bi bi-cash-stack',
          bg: 'bg-success'
        },

        {
          title: 'Sorties prévues',
          value: sortieDepassee,
          icon: 'bi bi-exclamation-triangle-fill',
          bg: sortieDepassee > 0
            ? 'bg-danger'
            : 'bg-success',
          alert: sortieDepassee > 0,
          message: sortieDepassee > 0
            ? 'Attention : sorties prévues'
            : 'Aucune sortie en retard'
        }

      ]

    },


    /* =========================================================
       PRODUITS
    ========================================================== */

    produitsKpi() {

      return [

        {
          title: 'Total Produits',
          value: this.totals.produits_count ?? 0,
          icon: 'bi bi-box-seam',
          bg: 'bg-primary'
        },

        {
          title: 'Médicaments',
          value: this.totals.medicaments_count ?? 0,
          icon: 'bi bi-capsule-pill',
          bg: 'bg-success'
        },

        {
          title: 'Consommables',
          value: this.totals.consommables_count ?? 0,
          icon: 'bi bi-bandaid',
          bg: 'bg-info'
        },

        {
          title: 'Équipements',
          value: this.totals.equipements_count ?? 0,
          icon: 'bi bi-hospital',
          bg: 'bg-secondary'
        },

        {
          title: 'Approvisionnements',
          value: this.totals.approvisionnements_count ?? 0,
          icon: 'bi bi-truck',
          bg: 'bg-warning'
        },

        {
          title: 'Lots',
          value: this.totals.lots_count ?? 0,
          icon: 'bi bi-boxes',
          bg: 'bg-dark'
        },

        {
          title: 'Ventes',
          value: this.totals.ventes_count ?? 0,
          icon: 'bi bi-cart-check',
          bg: 'bg-primary'
        },

        {
          title: 'Stocks critiques',
          value: this.totals.stocks_critiques_count ?? 0,
          icon: 'bi bi-exclamation-triangle',
          bg: 'bg-danger'
        },

        {
          title: 'Ruptures',
          value: this.totals.ruptures_count ?? 0,
          icon: 'bi bi-x-octagon',
          bg: 'bg-danger'
        },

        {
          title: 'Produits expirés',
          value: this.totals.expired_count ?? 0,
          icon: 'bi bi-calendar-x',
          bg: 'bg-danger'
        },

        {
          title: 'Expiration proche',
          value: this.totals.expiring_soon_count ?? 0,
          icon: 'bi bi-clock-history',
          bg: 'bg-warning'
        },

        {
          title: 'Valeur Stock',
          value: this.formatMontant(
            this.totals.valeur_stock ?? 0
          ),
          icon: 'bi bi-cash-stack',
          bg: 'bg-success'
        }

      ]

    },


    /* =========================================================
       CHART CONSULTATIONS
    ========================================================== */

    consultationsChartData() {

      return {

        labels: [
          'En cours',
          'En attente',
          'Terminées'
        ],

        datasets: [

          {
            label: 'Consultations',

            data: [

              Number(
                this.totals.consultation_en_cours ?? 0
              ),

              Number(
                this.totals.consultations_en_attente ?? 0
              ),

              Number(
                this.totals.consultations_terminees ?? 0
              )

            ],

            backgroundColor: [
              '#f59e0b',
              '#3b82f6',
              '#22c55e'
            ],

            borderColor: '#ffffff',

            borderWidth: 3,

            hoverOffset: 8

          }

        ]

      }

    },


    /* =========================================================
       CHART FINANCES
    ========================================================== */

    financesChartData() {

      return {

        labels: [
          'Patient',
          'Assurance',
          'Remboursement'
        ],

        datasets: [

          {
            label: 'Montant',

            data: [

              Number(
                this.totals.montant_encaisse_patient ?? 0
              ),

              Number(
                this.totals.montant_encaisse_assurance ?? 0
              ),

              Number(
                this.totals.remboursement ?? 0
              )

            ],

            backgroundColor: [
              '#22c55e',
              '#3b82f6',
              '#ef4444'
            ],

            borderColor: '#ffffff',

            borderWidth: 3,

            hoverOffset: 8

          }

        ]

      }

    },


    /* =========================================================
       CHART PHARMACIE
    ========================================================== */

    pharmacieChartData() {

      return {

        labels: [
          'Médicaments',
          'Consommables',
          'Équipements',
          'Stocks critiques',
          'Ruptures',
          'Expirés'
        ],

        datasets: [

          {
            label: 'Produits',

            data: [

              Number(
                this.totals.medicaments_count ?? 0
              ),

              Number(
                this.totals.consommables_count ?? 0
              ),

              Number(
                this.totals.equipements_count ?? 0
              ),

              Number(
                this.totals.stocks_critiques_count ?? 0
              ),

              Number(
                this.totals.ruptures_count ?? 0
              ),

              Number(
                this.totals.expired_count ?? 0
              )

            ],

            backgroundColor: [
              '#22c55e',
              '#06b6d4',
              '#64748b',
              '#f59e0b',
              '#ef4444',
              '#dc2626'
            ],

            borderRadius: 8,

            borderSkipped: false

          }

        ]

      }

    },


    /* =========================================================
       CHART HOSPITALISATIONS
    ========================================================== */

    hospitalisationsChartData() {

      return {

        labels: [
          'Total lits',
          'Lits occupés',
          'Lits libres',
          'Chambres occupées'
        ],

        datasets: [

          {
            label: 'Hospitalisation',

            data: [

              Number(
                this.totals.total_lits ?? 0
              ),

              Number(
                this.totals.lits_occupes ?? 0
              ),

              Number(
                this.totals.lits_libres ?? 0
              ),

              Number(
                this.totals.chambres_occupees ?? 0
              )

            ],

            backgroundColor: [
              '#3b82f6',
              '#ef4444',
              '#22c55e',
              '#f59e0b'
            ],

            borderRadius: 8,

            borderSkipped: false

          }

        ]

      }

    },


    /* =========================================================
       OPTIONS DOUGHNUT
    ========================================================== */

    doughnutOptions() {

      return {

        responsive: true,

        maintainAspectRatio: false,

        cutout: '65%',

        plugins: {

          legend: {
            position: 'bottom',

            labels: {
              usePointStyle: true,
              padding: 20
            }
          },

          tooltip: {
            enabled: true
          }

        }

      }

    },


    /* =========================================================
       OPTIONS BAR
    ========================================================== */

    barOptions() {

      return {

        responsive: true,

        maintainAspectRatio: false,

        plugins: {

          legend: {
            display: false
          },

          tooltip: {
            enabled: true
          }

        },

        scales: {

          y: {

            beginAtZero: true,

            ticks: {
              precision: 0
            }

          },

          x: {

            grid: {
              display: false
            }

          }

        }

      }

    }

  },


  mounted() {

    this.charger()

  },


  methods: {


    changerFiltre(val) {

      this.filtre = val

      this.charger()

    },


    async charger() {

      this.isLoading = true
      this.loadError = false

      try {

        const res =
          await api.getItems(
            `dashboard_data?periode=${this.filtre}`
          )

        /*
        |--------------------------------------------------------------------------
        | Protection importante
        |--------------------------------------------------------------------------
        */

        this.totals =
          res?.data &&
          typeof res.data === 'object'
            ? res.data
            : {}

      }

      catch (e) {

        console.error(
          'Erreur chargement dashboard :',
          e
        )

        this.totals = {}
        this.loadError = true

      }

      finally {

        this.isLoading = false

      }

    },


    formatMontant(n) {

      return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`

    }

  }

}

</script>


<style scoped>

/* =========================================================
   DASHBOARD
========================================================= */

.dashboard {
  background: #f8fafc;
  min-height: 100vh;
}


/* =========================================================
   HEADER
========================================================= */

.dashboard-header {

  background: #ffffff;

  border-radius: 18px;

  padding: 22px 25px;

  display: flex;

  justify-content: space-between;

  align-items: center;

  gap: 20px;

  box-shadow:
    0 4px 20px rgba(15, 23, 42, 0.06);

}


.header-title {

  display: flex;

  align-items: center;

  gap: 15px;

}


.header-icon {

  width: 55px;

  height: 55px;

  border-radius: 15px;

  display: flex;

  align-items: center;

  justify-content: center;

  background: linear-gradient(
    135deg,
    #2563eb,
    #7c3aed
  );

  color: white;

  font-size: 1.6rem;

}


.filters {

  display: flex;

  flex-wrap: wrap;

  gap: 8px;

}


.filter-btn {

  border-radius: 10px;

  font-weight: 500;

}


.filter-active {

  background: linear-gradient(
    135deg,
    #2563eb,
    #7c3aed
  );

  border: none;

  color: white;

}


.filter-normal {

  background: white;

  border: 1px solid #dbe3ef;

  color: #334155;

}


.filter-normal:hover {

  background: #f1f5f9;

}


/* =========================================================
   LOADER
========================================================= */

.loading-container {

  min-height: 400px;

  display: flex;

  flex-direction: column;

  justify-content: center;

  align-items: center;

}


/* =========================================================
   SECTIONS
========================================================= */

.dashboard-section {

  margin-bottom: 45px;

}


.section-title {

  display: flex;

  align-items: center;

  gap: 10px;

  font-size: 1.15rem;

  font-weight: 700;

  margin-bottom: 18px;

  padding-left: 12px;

  border-left: 5px solid;

}


.primary-title {
  color: #2563eb;
  border-color: #2563eb;
}


.success-title {
  color: #16a34a;
  border-color: #16a34a;
}


.danger-title {
  color: #dc2626;
  border-color: #dc2626;
}


.warning-title {
  color: #d97706;
  border-color: #d97706;
}


.info-title {
  color: #0891b2;
  border-color: #0891b2;
}


.chart-title {
  color: #7c3aed;
  border-color: #7c3aed;
}


/* =========================================================
   KPI CARDS
========================================================= */

.kpi-card {

  background: white;

  border-radius: 16px;

  padding: 20px;

  min-height: 105px;

  display: flex;

  align-items: center;

  gap: 15px;

  box-shadow:
    0 4px 18px rgba(15, 23, 42, 0.06);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

}


.kpi-card:hover {

  transform: translateY(-4px);

  box-shadow:
    0 10px 28px rgba(15, 23, 42, 0.10);

}


.kpi-icon {

  width: 50px;

  height: 50px;

  min-width: 50px;

  border-radius: 14px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: white;

  font-size: 1.25rem;

}


.kpi-title {

  color: #64748b;

  font-size: 0.82rem;

  margin-bottom: 4px;

}


.kpi-value {

  color: #0f172a;

  font-size: 1.25rem;

  font-weight: 700;

}


.alert-card {

  border: 1px solid #fecaca;

  background: #fffafa;

}


/* =========================================================
   CHART CARDS
========================================================= */

.chart-card {

  background: white;

  border-radius: 18px;

  padding: 22px;

  box-shadow:
    0 5px 25px rgba(15, 23, 42, 0.07);

  height: 100%;

}


.chart-header {

  display: flex;

  justify-content: space-between;

  align-items: center;

  margin-bottom: 15px;

}


.chart-header h5 {

  margin: 0;

  font-weight: 700;

  color: #1e293b;

}


.chart-header h5 i {

  margin-right: 8px;

}


.chart-header small {

  color: #94a3b8;

}


.chart-container {

  position: relative;

  height: 300px;

}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 992px) {

  .dashboard-header {

    flex-direction: column;

    align-items: flex-start;

  }

  .filters {

    width: 100%;

  }

}


@media (max-width: 576px) {

  .dashboard-header {

    padding: 18px;

  }

  .header-title {

    align-items: flex-start;

  }

  .filters {

    width: 100%;

  }

  .filter-btn {

    flex: 1;

  }

  .chart-container {

    height: 260px;

  }

}

</style>